'use client'

import * as React from 'react'
import * as DropdownMenuPrimitive from '@radix-ui/react-dropdown-menu'

import dynamic from 'next/dynamic'

import { cn } from '../../../lib/utils'
import { cva, type VariantProps } from 'class-variance-authority'
import Icon, { iconTypes } from '../Icon'
import Link from 'next/link'
import { type AvatarProps } from '../Avatar'

const Avatar = dynamic(() => import('../Avatar').then((mod) => mod.Avatar), {
  ssr: false,
})
const AvatarFallback = dynamic(
  () => import('../Avatar').then((mod) => mod.AvatarFallback),
  { ssr: false },
)
const AvatarImage = dynamic(
  () => import('../Avatar').then((mod) => mod.AvatarImage),
  { ssr: false },
)
const Switch = dynamic(() => import('../Switch').then((mod) => mod.Switch), {
  ssr: false,
})

const dropdownVariants = cva('', {
  variants: {
    variant: {
      default: 'p-1 text-sm',
      workplace: 'bg-card p-1 md:text-xs text-sm',
    },
    size: {
      default: 'w-auto',
      md: 'w-[300px]',
    },
  },
})

const dropdownTriggerVariants = cva(
  'inline-flex gap-2 items-center justify-center whitespace-nowrap rounded-md text-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 leading-none',
  {
    variants: {
      variant: {
        default:
          'bg-primary text-primary-foreground shadow hover:bg-primary/90 ',
        destructive:
          'bg-destructive text-destructive-foreground shadow-sm hover:bg-destructive/90',
        outline:
          'border border-input bg-background text-foreground shadow-sm hover:bg-accent hover:text-accent-foreground',
        secondary:
          'bg-secondary text-secondary-foreground shadow-sm hover:bg-secondary/90',
        sideMenu: 'bg-background hover:bg-accent hover:text-accent-foreground',
        ghost: 'hover:bg-accent hover:text-accent-foreground',
        inset: 'bg-accent hover:bg-accent/50 text-accent-foreground',
        link: 'text-primary underline-offset-4 hover:underline',
        skeleton: 'bg-secondary animate-pulse w-12',
      },
      size: {
        default: 'h-7 px-4 py-2',
        sm: 'h-7 rounded-md px-2 text-xs ease-in ',
        lg: 'h-10 rounded-md px-4 gap-4',
        xl: 'h-16 rounded-md px-5 ',
        icon: 'h-9 w-9',
        secondaryAction: 'px-2 py-1.5 text-xs',
        action: 'h-12 w-full px-4',
      },
      contentPosition: {
        left: 'justify-start',
        right: 'justify-end',
        center: 'justify-center',
      },
    },
  },
)

const DropdownMenuComponent = DropdownMenuPrimitive.Root

const DropdownMenuTrigger = DropdownMenuPrimitive.Trigger

const DropdownMenuGroup = DropdownMenuPrimitive.Group

const DropdownMenuPortal = DropdownMenuPrimitive.Portal

const DropdownMenuSub = DropdownMenuPrimitive.Sub

const DropdownMenuRadioGroup = DropdownMenuPrimitive.RadioGroup

const DropdownMenuSubTrigger = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.SubTrigger>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.SubTrigger> & {
    inset?: boolean
  }
>(({ className, inset, children, ...props }, ref) => (
  <DropdownMenuPrimitive.SubTrigger
    ref={ref}
    className={cn(
      'flex cursor-default select-none items-center rounded-sm px-2 py-1.5 outline-none focus:bg-accent data-[state=open]:bg-accent',
      inset && 'pl-8',
      className,
    )}
    {...props}
  >
    {children}
    <Icon name="ChevronRight" className="ml-auto h-4 w-4 shrink-0 opacity-60" />
  </DropdownMenuPrimitive.SubTrigger>
))
DropdownMenuSubTrigger.displayName =
  DropdownMenuPrimitive.SubTrigger.displayName

const DropdownMenuSubContent = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.SubContent>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.SubContent>
>(({ className, ...props }, ref) => (
  <DropdownMenuPrimitive.SubContent
    ref={ref}
    className={cn(
      'z-50 min-w-[8rem] max-w-[90vw] overflow-hidden rounded-md border bg-popover p-1 text-popover-foreground shadow-lg data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2',
      className,
    )}
    {...props}
  />
))
DropdownMenuSubContent.displayName =
  DropdownMenuPrimitive.SubContent.displayName

const DropdownMenuContent = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Content>
>(({ className, sideOffset = 4, ...props }, ref) => (
  <DropdownMenuPrimitive.Portal>
    <DropdownMenuPrimitive.Content
      ref={ref}
      sideOffset={sideOffset}
      className={cn(
        'z-50 min-w-[8rem] max-w-[90vw] overflow-hidden rounded-md border bg-popover p-1 text-popover-foreground shadow-md',
        'data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2',
        className,
      )}
      {...props}
    />
  </DropdownMenuPrimitive.Portal>
))
DropdownMenuContent.displayName = DropdownMenuPrimitive.Content.displayName

const DropdownMenuItem = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Item> & {
    inset?: boolean
  }
>(({ className, inset, ...props }, ref) => (
  <DropdownMenuPrimitive.Item
    ref={ref}
    className={cn(
      'relative flex cursor-pointer select-none items-center rounded-sm py-1 outline-none transition-colors focus:bg-accent focus:text-accent-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50',
      inset && 'pl-8',
      className,
    )}
    {...props}
  />
))
DropdownMenuItem.displayName = DropdownMenuPrimitive.Item.displayName

const DropdownMenuCheckboxItem = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.CheckboxItem>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.CheckboxItem>
>(({ className, children, checked, ...props }, ref) => (
  <DropdownMenuPrimitive.CheckboxItem
    ref={ref}
    className={cn(
      'relative flex cursor-pointer select-none items-center rounded-sm py-1.5 pl-8 pr-2 outline-none transition-colors focus:bg-accent focus:text-accent-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50',
      className,
    )}
    checked={checked}
    {...props}
  >
    <span className="absolute left-2 flex h-3.5 w-3.5 items-center justify-center">
      <DropdownMenuPrimitive.ItemIndicator>
        <Icon name="Check" />
      </DropdownMenuPrimitive.ItemIndicator>
    </span>
    {children}
  </DropdownMenuPrimitive.CheckboxItem>
))
DropdownMenuCheckboxItem.displayName =
  DropdownMenuPrimitive.CheckboxItem.displayName

const DropdownMenuRadioItem = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.RadioItem>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.RadioItem>
>(({ className, children, ...props }, ref) => (
  <DropdownMenuPrimitive.RadioItem
    ref={ref}
    className={cn(
      'relative flex cursor-pointer select-none items-center rounded-sm py-1.5 pl-8 pr-2 outline-none transition-colors focus:bg-accent focus:text-accent-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50',
      className,
    )}
    {...props}
  >
    <span className="absolute left-2 flex h-3.5 w-3.5 items-center justify-center">
      <DropdownMenuPrimitive.ItemIndicator>
        <Icon name="Check" />
      </DropdownMenuPrimitive.ItemIndicator>
    </span>
    {children}
  </DropdownMenuPrimitive.RadioItem>
))
DropdownMenuRadioItem.displayName = DropdownMenuPrimitive.RadioItem.displayName

const DropdownMenuLabel = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.Label>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Label> & {
    inset?: boolean
    size: 'sm' | 'default' | 'lg'
  }
>(({ className, size, inset, ...props }, ref) => (
  <DropdownMenuPrimitive.Label
    ref={ref}
    className={cn('px-2 py-1.5', inset && 'pl-8', className, {
      'text-2xs': size === 'sm',
      'text-sm': size === 'default',
      'text-lg': size === 'lg',
    })}
    {...props}
  />
))
DropdownMenuLabel.displayName = DropdownMenuPrimitive.Label.displayName

const DropdownMenuSeparator = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.Separator>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Separator>
>(({ className, ...props }, ref) => (
  <DropdownMenuPrimitive.Separator
    ref={ref}
    className={cn('-mx-1 my-1 h-px bg-muted', className)}
    {...props}
  />
))
DropdownMenuSeparator.displayName = DropdownMenuPrimitive.Separator.displayName

const DropdownMenuShortcut = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLSpanElement>) => {
  return (
    <span
      className={cn(
        'ml-auto tracking-widest opacity-60 text-sm md:text-xs flex-shrink-0',
        className,
      )}
      {...props}
    />
  )
}
DropdownMenuShortcut.displayName = 'DropdownMenuShortcut'

const generateMenuContent = (
  components: DropdownMenuComponent[],
  variant?: VariantProps<typeof dropdownVariants>['variant'],
  size?: VariantProps<typeof dropdownVariants>['size'],
) => {
  // do any of the components have an icon
  const hasAnyIcon = components?.some(
    (component) => 'icon' in component.props && component.props.icon,
  )
  const hasAnyRightComponent = components?.some(
    (component) =>
      'shortcut' in component.props ||
      component.type === 'switch' ||
      'isSelected' in component.props,
  )

  const iconWrapper = (i: iconTypes) => {
    return (
      <div className="flex justify-center items-center w-6 h-6 flex-shrink-0">
        <Icon name={i} />
      </div>
    )
  }

  const labelWrapper = (
    label: string,
    description?: string,
    truncate?: boolean,
  ) => {
    return (
      <span
        className={cn(
          'flex flex-col min-w-0 flex-1',
          {
            truncate: truncate,
          },
          'text-xs',
        )}
      >
        <span className={cn('truncate', { 'text-ellipsis': truncate })}>
          {label}
        </span>
        {description && (
          <div
            className={cn('text-2xs truncate', { 'text-ellipsis': truncate })}
          >
            {description}
          </div>
        )}
      </span>
    )
  }

  const generateItemComponent = (
    props: DropdownMenuItemComponent['props'],
    index: number,
    description?: string,
  ) => {
    const {
      title,
      label,
      icon,
      avatar,
      isSelected,
      shortcut,
      truncate = false,
      isDisabled,
      handleClick,
    } = props

    return (
      <DropdownMenuItem
        disabled={isDisabled}
        onClick={handleClick}
        key={index}
        className="grid grid-cols-12 gap-4 w-full"
      >
        {/* left components */}
        <div
          className={cn(
            {
              ['col-span-10']: hasAnyRightComponent,
              ['col-span-12']: !hasAnyRightComponent,
            },
            'flex items-center gap-4 justify-between h-7 px-2 min-w-0',
          )}
          title={`action-${title || label}`}
        >
          <div className="flex flex-row items-center gap-1 min-w-0 flex-1">
            {(icon || avatar) && (
              <div className="flex items-center justify-center flex-shrink-0">
                {icon && iconWrapper(icon)}
                {avatar && (
                  <Avatar className="h-6 w-6 rounded">
                    <AvatarImage src={avatar.img} />
                    <AvatarFallback>{avatar.initials}</AvatarFallback>
                  </Avatar>
                )}
              </div>
            )}
            {!icon && !avatar && hasAnyIcon && (
              <span className="w-6 flex-shrink-0" />
            )}
            {labelWrapper(label, description, truncate)}
          </div>
        </div>
        {/* right components */}
        {hasAnyRightComponent && (
          <div className="col-span-2 flex gap-2 justify-end items-center px-2 flex-shrink-0">
            {isSelected && <Icon name="Check" />}
            {shortcut && (
              <DropdownMenuShortcut>{shortcut}</DropdownMenuShortcut>
            )}
          </div>
        )}
      </DropdownMenuItem>
    )
  }

  return (
    <>
      {components.map((component, index) => {
        const { type, props } = component
        if (type === 'label') {
          const { size, className, label } = props
          return (
            <DropdownMenuLabel key={index} className={className} size={size}>
              {label}
            </DropdownMenuLabel>
          )
        }

        if (type === 'separator') {
          return <DropdownMenuSeparator key={index} />
        }

        if (type === 'link') {
          const { icon, label, href, description, truncate = false } = props
          return (
            <Link
              href={href}
              key={index}
              className="relative grid grid-cols-12 gap-4 w-full h-9 block cursor-pointer select-none items-center rounded-sm px-2 py-1.5 outline-none transition-colors hover:bg-accent hover:text-accent-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50"
            >
              <div
                className={cn('flex gap-2 items-center justify-start min-w-0', {
                  'col-span-12': !hasAnyRightComponent,
                  'col-span-10': hasAnyRightComponent,
                })}
              >
                {icon && (
                  <div className="flex-shrink-0">{iconWrapper(icon)}</div>
                )}
                {!icon && hasAnyIcon && <span className="w-6 flex-shrink-0" />}
                {labelWrapper(label, description, truncate)}
              </div>
            </Link>
          )
        }

        if (type === 'switch') {
          const { label, title, value, icon, handleChange } = props
          return (
            <div
              onClick={() => handleChange(!value)}
              className="relative cursor-pointer select-none items-center rounded-sm px-2 py-1.5 outline-none transition-colors hover:bg-accent hover:text-accent-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50 grid grid-cols-12 gap-4 w-full h-9"
              title={title || label}
              key={index}
            >
              {/* left components */}
              <div className="col-span-10 flex gap-2 items-center min-w-0">
                {icon && (
                  <div className="flex-shrink-0">{iconWrapper(icon)}</div>
                )}
                {!icon && hasAnyIcon && <span className="w-6 flex-shrink-0" />}
                {labelWrapper(label)}
              </div>
              {/* right components */}
              <div className="col-span-2 flex gap-4 justify-end items-center flex-shrink-0">
                <Switch bgSize="small" toggleSize="small" isChecked={value} />
              </div>
            </div>
          )
        }

        if (type === 'item') {
          return generateItemComponent(props, index, props.description)
        }

        if (type === 'list') {
          const { className, components, maxHeight = 300 } = props

          return (
            <div
              key={index}
              className={cn('flex flex-col gap-1', className)}
              style={{
                maxHeight: `${maxHeight}px`,
                overflowY: 'auto',
              }}
            >
              {components.map((component, index) => {
                const { props } = component
                return generateItemComponent(props, index, props.description)
              })}
            </div>
          )
        }

        if (type === 'submenu') {
          const { icon, label, components } = props
          return (
            <DropdownMenuGroup key={index}>
              <DropdownMenuSub>
                <DropdownMenuSubTrigger className="gap-2 h-9 min-w-0">
                  {icon && (
                    <div className="flex-shrink-0">{iconWrapper(icon)}</div>
                  )}
                  {!icon && hasAnyIcon && (
                    <span className="w-6 flex-shrink-0" />
                  )}
                  {labelWrapper(label)}
                </DropdownMenuSubTrigger>
                <DropdownMenuPortal>
                  <DropdownMenuSubContent
                    className={cn(dropdownVariants({ variant, size }))}
                  >
                    {generateMenuContent(components, variant, size)}
                  </DropdownMenuSubContent>
                </DropdownMenuPortal>
              </DropdownMenuSub>
            </DropdownMenuGroup>
          )
        }

        if (type === 'custom') {
          return (
            <div key={index} className="w-full">
              {props.render()}
            </div>
          )
        }
      })}
    </>
  )
}

type DropdownMenuCheckboxItemComponent = {
  type: 'switch'
  props: {
    value: boolean
    title?: string
    label: string
    icon?: iconTypes
    handleChange: (value: boolean) => void | Promise<void>
  }
}

type DropdownMenuLabelComponent = {
  type: 'label'
  props: {
    label: string
    size: 'sm' | 'default' | 'lg'
    className?: string
  }
}

type DropdownMenuSeperatorComponent = {
  type: 'separator'
  props: {
    children?: string
  }
}

type DropdownMenuSubMenuComponent = {
  type: 'submenu'
  props: {
    label: string
    icon?: iconTypes
    components: DropdownMenuComponent[]
  }
}

type DropdownMenuCustomComponent = {
  type: 'custom'
  props: {
    render: () => React.ReactNode
  }
}

export type DropdownMenuItemComponent = {
  type: 'item'
  props: {
    title?: string
    value?: string
    isSelected?: boolean
    isLoading?: boolean
    isDisabled?: boolean
    label: string
    description?: string
    shortcut?: string
    icon?: iconTypes
    truncate?: boolean
    avatar?: AvatarProps
    handleClick: () => void | Promise<void>
  }
}

export type DropdownMenuLinkComponent = {
  type: 'link'
  props: {
    value?: string
    isSelected?: boolean
    isLoading?: boolean
    icon?: iconTypes
    label: string
    description?: string
    href: string
    truncate?: boolean
  }
}

export type DropdownMenuItemListComponent = {
  type: 'list'
  props: {
    className?: string
    maxHeight?: number
    components: DropdownMenuItemComponent[]
  }
}

type DropdownMenuComponent =
  | DropdownMenuLabelComponent
  | DropdownMenuSeperatorComponent
  | DropdownMenuSubMenuComponent
  | DropdownMenuItemComponent
  | DropdownMenuCheckboxItemComponent
  | DropdownMenuLinkComponent
  | DropdownMenuItemListComponent
  | DropdownMenuCustomComponent

type TriggerProps = {
  label: string
  title?: string
  variant?:
    | 'default'
    | 'destructive'
    | 'outline'
    | 'secondary'
    | 'sideMenu'
    | 'ghost'
    | 'inset'
    | 'link'
    | 'skeleton'
  leftIcon?: iconTypes
  rightIcon?: iconTypes
  className?: string
  isDisabled?: boolean
  isLoading?: boolean
  avatar?: AvatarProps
  avatarClassName?: string
  truncate?: boolean
  contentPosition?: 'left' | 'right' | 'center'
  size?: 'sm' | 'default' | 'lg' | 'icon' | 'secondaryAction' | 'action' | 'xl'
  rightLabel?: string
}

export interface DropdownMenuProps
  extends VariantProps<typeof dropdownVariants> {
  align?: 'start' | 'center' | 'end'
  defaultOpen?: boolean
  isDisabled?: boolean
  width?: 'full' | 'auto'
  trigger: TriggerProps
  components: DropdownMenuComponent[]
}

export const DropdownMenu = (props: DropdownMenuProps) => {
  const { variant, size, align, width = 'full' } = props

  const {
    isDisabled,
    className,
    leftIcon,
    rightIcon,
    title,
    label,
    contentPosition,
    isLoading,
    avatar,
    avatarClassName,
    truncate,
  } = props.trigger
  return (
    <DropdownMenuComponent {...props} modal={false}>
      <DropdownMenuTrigger
        disabled={isDisabled}
        title={`action-${title || label}`}
        className={cn(
          'h-7 px-4',
          width === 'full' ? 'w-full' : 'w-auto',
          dropdownTriggerVariants({
            variant: props.trigger.variant || 'outline',
            size: props.trigger.size,
            contentPosition,
          }),
          { 'truncate overflow-hidden': truncate },
          className,
        )}
      >
        <div className="flex items-center gap-2 w-full min-w-0">
          {(leftIcon || isLoading || avatar) && (
            <div className="flex items-center justify-center flex-shrink-0">
              {leftIcon && !isLoading && <Icon name={leftIcon} size={15} />}
              {isLoading && (
                <Icon name="LoaderCircle" size={15} className="animate-spin" />
              )}
              {avatar && !isLoading && (
                <Avatar className={cn('h-6 w-6 rounded', avatarClassName)}>
                  <AvatarImage src={avatar.img} />
                  <AvatarFallback>{avatar.initials}</AvatarFallback>
                </Avatar>
              )}
            </div>
          )}
          <p
            className={cn('min-w-0 flex-1 truncate text-left', {
              'text-ellipsis': truncate,
            })}
          >
            {props.trigger.label}
          </p>
          {props.trigger.rightLabel && (
            <span className="ml-auto text-muted-foreground/70">
              {props.trigger.rightLabel}
            </span>
          )}
          {rightIcon && !isLoading && (
            <Icon name={rightIcon} size={15} className="flex-shrink-0" />
          )}
        </div>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align={align}
        className={cn(dropdownVariants({ variant, size }))}
      >
        {generateMenuContent(props.components, variant, size)}
      </DropdownMenuContent>
    </DropdownMenuComponent>
  )
}

export {
  DropdownMenuComponent,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuCheckboxItem,
  DropdownMenuRadioItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuShortcut,
  DropdownMenuGroup,
  DropdownMenuPortal,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuRadioGroup,
}
